// src/api/adduser.js
import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { notifySuccess, notifyError } from "../utils/ToastifyProvider";
import { useNavigate } from "react-router";
import { useLoader } from "../utils/LoaderContext";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const createUser = async (data) => {
  const token = localStorage.getItem("token");
  const payload = {
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    mobile: data.phone,
    gender: data.gender,
    companyName: data.companyName,
    role: "user", // default role
  };
  const response = await axios.post(`${BASE_URL}/create-users`, payload, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const useCreateUser = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoader();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      setLoading(true); // ✅ Start
      try {
        return await createUser(data);
      } finally {
        setLoading(false); // ✅ End
      }
    },
    onSuccess: (data) => {
      setLoading(true);
      // console.log("User created successfully:", data);
      notifySuccess("User created successfully");
      setLoading(false);
      navigate("/user");
      queryClient.invalidateQueries(["users"]);
    },
    onError: (error) => {
      console.error("Error creating user:", error);
      notifyError(error?.response?.data?.message || "Failed to create user");
    },
  });
};

const getAllUsers = async () => {
  const token = localStorage.getItem("token");

  const { data } = await axios.get(`${BASE_URL}/get-allusers`, {
    headers: {
      Authorization: `Bearer ${token}`,
      role:"admin",
    },
  });

  return data; // data IS an array
};


export const useGetAllUsers = () => {
  return useQuery({
    queryKey: ["all-users"],
    queryFn: getAllUsers,
    staleTime: 1000 * 60 * 5,
    onError: (error) => {
      console.error("Error fetching users:", error);
      notifyError("Failed to fetch users");
    },
  });
};

export const useGetUserById = (userId) => {
  const token = localStorage.getItem("token");
  return useQuery({
    queryKey: ["admin", userId],
    queryFn: async () => {
      const res = await axios.get(`${BASE_URL}/getuser/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status !== 200) {
        throw new Error("Failed to fetch user data");
      }
      return res.data;
    },
    enabled: !!userId,
    onError: (error) => {
      console.error("Error fetching user by ID:", error);
      notifyError("Failed to fetch user details");
    },
  });
};

const updateUser = async ({ userId, data }) => {
  const token = localStorage.getItem("token");
  const payload = {
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    mobile: data.phone,
    gender: data.gender,
    companyName: data.companyName,
    role: "user",
  };

  const response = await axios.put(
    `${BASE_URL}/updateuser/${userId}`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return response.data;
};

export const useUpdateUser = () => {
  const { setLoading } = useLoader();
  return useMutation({
    mutationFn: async (params) => {
      setLoading(true); // ✅ Start
      try {
        return await updateUser(params);
      } finally {
        setLoading(false); // ✅ End
      }
    },
    onSuccess: () => {
      notifySuccess("User updated successfully");
    },
    onError: (error) => {
      notifyError(error?.response?.data?.message || "Failed to update user");
    },
  });
};

const deleteUser = async (userId) => {
  const token = localStorage.getItem("token");
  const res = await axios.delete(`${BASE_URL}/delete-users/${userId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return res.data;
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  const { setLoading } = useLoader();

  return useMutation({
    mutationFn: async (userId) => {
      setLoading(true); // ✅ Start
      try {
        return await deleteUser(userId);
      } finally {
        setLoading(false); // ✅ End
      }
    },
    onSuccess: () => {
      notifySuccess("User deleted successfully");
      queryClient.refetchQueries(["users"]);
    },
    onError: (error) => {
      notifyError(error?.response?.data?.message || "Failed to delete user");
    },
  });
};
